import axios, { type AxiosError, type AxiosInstance } from 'axios'
import { env } from '@/lib/env'
import { readStorage, STORAGE_KEYS, writeStorage } from '@/lib/storage'

/** Requests that take longer than this are aborted; large files go to Spaces, not through this client. */
export const API_TIMEOUT_MS = 30_000

/**
 * Axios instance shared by every API module. Nothing outside `src/api` should import axios.
 * Mirrors vidchops-fe/src/api/httpClient.js: the same three headers are sent
 * (Authorization, website-type-id, user-time-zone) and the same localStorage keys are used.
 *
 * Token lifecycle: the Laravel API issues a JWT at /api/login and has no refresh endpoint, so
 * there is nothing to silently renew. When the token expires the API answers 401, the interceptor
 * below clears the session and AuthProvider sends the user back to /login.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl || undefined,
  timeout: API_TIMEOUT_MS,
  headers: { Accept: 'application/json' },
})

export function setAuthToken(token: string | null): void {
  if (token) {
    writeStorage(STORAGE_KEYS.authToken, token)
    apiClient.defaults.headers.common.Authorization = token
  } else {
    writeStorage(STORAGE_KEYS.authToken, null)
    delete apiClient.defaults.headers.common.Authorization
  }
}

export function setWebsiteType(id: string | number | null): void {
  if (id !== null && id !== undefined && id !== '') {
    writeStorage(STORAGE_KEYS.websiteType, String(id))
    apiClient.defaults.headers.common['website-type-id'] = String(id)
  } else {
    writeStorage(STORAGE_KEYS.websiteType, null)
    delete apiClient.defaults.headers.common['website-type-id']
  }
}

export function setUserTimezone(timezone: string | null): void {
  if (timezone && timezone !== 'none') {
    writeStorage(STORAGE_KEYS.timezone, timezone)
    apiClient.defaults.headers.common['user-time-zone'] = timezone
  } else {
    writeStorage(STORAGE_KEYS.timezone, null)
    delete apiClient.defaults.headers.common['user-time-zone']
  }
}

export function getStoredToken(): string | null {
  return readStorage(STORAGE_KEYS.authToken)
}

/** Restore headers from a previous session. Call once at startup. */
export function hydrateClientFromStorage(): void {
  const token = readStorage(STORAGE_KEYS.authToken)
  if (token) apiClient.defaults.headers.common.Authorization = token
  const website = readStorage(STORAGE_KEYS.websiteType)
  if (website) apiClient.defaults.headers.common['website-type-id'] = website
  const tz = readStorage(STORAGE_KEYS.timezone)
  if (tz && tz !== 'none') apiClient.defaults.headers.common['user-time-zone'] = tz
}

type SessionExpiredListener = () => void
const sessionExpiredListeners = new Set<SessionExpiredListener>()

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener)
  return () => sessionExpiredListeners.delete(listener)
}

/** Clears the session when the API says the token is dead or the website is not accessible. */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string }>) => {
    const status = error.response?.status
    const code = error.response?.data?.error
    const isAuthRequest = error.config?.url?.includes('/api/login')
    if (!isAuthRequest && (code === 'Not logged in' || status === 401 || (status === 403 && code === 'website_access_denied'))) {
      setAuthToken(null)
      setWebsiteType(null)
      sessionExpiredListeners.forEach((listener) => listener())
    }
    return Promise.reject(error)
  },
)

/** HTTP status of a failed API call, or undefined when the failure was not an HTTP response. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined
}

/** Extracts the most useful human-readable message from an API error. */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong, please try again'): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      { message?: string; error?: string; error_message?: string; errors?: Record<string, string[]> } | undefined
    if (data?.errors) {
      const first = Object.values(data.errors)[0]
      if (first?.length) return first[0]
    }
    return data?.message || data?.error_message || (typeof data?.error === 'string' ? data.error : '') || error.message || fallback
  }
  if (error instanceof Error) return error.message || fallback
  return fallback
}
